import { useMemo } from 'react';

import { IOperatingHours } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useConfigValue } from '@fhs-legacy/frontend/src/hooks/configs/use-config-value';
import { readableTimeInterval } from '@fhs-legacy/frontend/src/utils/restaurant';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from '@fhs-legacy/frontend/src/utils/restaurant/constants';

export interface WeeklyHoursRow {
  /** `IOperatingHours` field prefix, e.g. `mon` — also the row key. */
  day: string;
  label: string;
  /** Already-formatted interval, or the closed fallback when the day has no hours. */
  intervalText: string;
}

/**
 * The design lists a plain Monday-to-Sunday week, unlike the legacy store info modal, which
 * rotates the list so today comes first. No day is weighted differently from the others.
 */
const WEEK: { day: string; label: string }[] = [
  { day: 'mon', label: 'Monday' },
  { day: 'tue', label: 'Tuesday' },
  { day: 'wed', label: 'Wednesday' },
  { day: 'thr', label: 'Thursday' },
  { day: 'fri', label: 'Friday' },
  { day: 'sat', label: 'Saturday' },
  { day: 'sun', label: 'Sunday' },
];

/** Turns one set of operating hours into the seven rows of the Store Hours list. */
export function useWeeklyHours(hours: IOperatingHours | null | undefined): WeeklyHoursRow[] {
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });

  return useMemo(() => {
    if (!hours) {
      return [];
    }

    const formatInterval = readableTimeInterval({
      fallbackMessage: 'Closed',
      open24HoursMessage: 'Open 24 hours',
      timeFormat: timeFormatConfig,
    });

    return WEEK.map(({ day, label }) => ({
      day,
      label,
      intervalText: formatInterval(hours[`${day}Open`], hours[`${day}Close`]),
    }));
  }, [hours, timeFormatConfig]);
}
